import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../services/api";

const UIWagesPage = () => {
  const { id: businessId } = useParams();

  const [payDates, setPayDates] = useState([]);
  const [selectedPayDate, setSelectedPayDate] = useState("");
  const [rows, setRows] = useState([]);

  const [loadingDates, setLoadingDates] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState("");

  // EDIT STATE (one record at a time)
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    state_ui: "",
    state_ui_taxable: "",
    federal_ui: "",
    taxable_wages: "",
  });
  const [saving, setSaving] = useState(false);

  const toYYYYMMDD = (value) => {
    if (!value) return "";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    if (typeof value === "string" && value.includes("T")) return value.split("T")[0];

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const displayDate = (yyyyMmDd) => {
    if (!yyyyMmDd) return "";
    return new Date(`${yyyyMmDd}T00:00:00`).toLocaleDateString();
  };

  useEffect(() => {
    const fetchPayDates = async () => {
      try {
        setLoadingDates(true);
        setError("");

        const res = await api.get(`/payroll/paydates/${businessId}`);
        const dates = res.data || [];
        setPayDates(dates);

        if (dates.length > 0) {
          const first = toYYYYMMDD(dates[0].PAY_DATE || dates[0].pay_date);
          setSelectedPayDate(first);
        }
      } catch (err) {
        console.error("Fetch pay dates error:", err);
        setError("Failed to fetch pay dates.");
      } finally {
        setLoadingDates(false);
      }
    };

    fetchPayDates();
  }, [businessId]);

  useEffect(() => {
    const fetchRows = async () => {
      if (!selectedPayDate) return;

      try {
        setLoadingRows(true);
        setError("");

        // clear editing state when switching pay date
        setEditingId(null);

        const payDateParam = toYYYYMMDD(selectedPayDate);

        const res = await api.get(`/payroll/ui-wages/${businessId}`, {
          params: { pay_date: payDateParam },
        });

        setRows(res.data || []);
      } catch (err) {
        console.error("Fetch UI wages error:", err);
        setError("Failed to fetch UI / taxable wages for this pay date.");
      } finally {
        setLoadingRows(false);
      }
    };

    fetchRows();
  }, [businessId, selectedPayDate]);

  const fmt = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return "0.00";
    return n.toFixed(2);
  };

  const startEdit = (row) => {
    const id = row.ID || row.id;

    setEditingId(id);
    setEditForm({
      state_ui: row.state_ui ?? "",
      state_ui_taxable: row.state_ui_taxable ?? "",
      federal_ui: row.federal_ui ?? "",
      taxable_wages: row.taxable_wages ?? "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({
      state_ui: "",
      state_ui_taxable: "",
      federal_ui: "",
      taxable_wages: "",
    });
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveEdit = async () => {
    if (!editingId) return;

    try {
      setSaving(true);
      setError("");

      await api.put(`/payroll/ui-wages/${editingId}`, {
        state_ui: editForm.state_ui,
        state_ui_taxable: editForm.state_ui_taxable,
        federal_ui: editForm.federal_ui,
        taxable_wages: editForm.taxable_wages,
      });

      // Update locally so no refetch needed
      setRows((prev) =>
        prev.map((r) => {
          const id = r.ID || r.id;
          if (id !== editingId) return r;

          return {
            ...r,
            state_ui: editForm.state_ui === "" ? null : Number(editForm.state_ui),
            state_ui_taxable: editForm.state_ui_taxable === "" ? null : Number(editForm.state_ui_taxable),
            federal_ui: editForm.federal_ui === "" ? null : Number(editForm.federal_ui),
            taxable_wages: editForm.taxable_wages === "" ? null : Number(editForm.taxable_wages),
          };
        })
      );

      cancelEdit();
    } catch (err) {
      console.error("Save UI wages error:", err);
      setError("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loadingDates) return <p>Loading pay dates...</p>;

  return (
    <div className="uiw-body">
      <h2>UI / Taxable Wages</h2>

      {error ? <p className="uiw-error">{error}</p> : null}

      <div className="uiw-controls">
        <label>Pay Date:</label>
        <select
          value={selectedPayDate}
          onChange={(e) => setSelectedPayDate(toYYYYMMDD(e.target.value))}
          disabled={payDates.length === 0}
        >
          {payDates.length === 0 ? (
            <option value="">No pay dates found</option>
          ) : (
            payDates.map((d) => {
              const val = toYYYYMMDD(d.PAY_DATE || d.pay_date);
              return (
                <option key={val} value={val}>
                  {displayDate(val)}
                </option>
              );
            })
          )}
        </select>
      </div>

      {loadingRows ? (
        <p>Loading employees...</p>
      ) : (
        <table className="uiw-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>State UI</th>
              <th>State UI Taxable</th>
              <th>Federal UI</th>
              <th>Taxable Wages</th>
              <th style={{ width: 160 }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center" }}>
                  No payroll records found for this pay date.
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const id = r.ID || r.id;
                const name = r.NAME || r.name;
                const isEditing = editingId === id;

                return (
                  <tr key={id}>
                    <td>{name}</td>

                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editForm.state_ui}
                          onChange={(e) => handleEditChange("state_ui", e.target.value)}
                        />
                      ) : (
                        `$${fmt(r.state_ui)}`
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editForm.state_ui_taxable}
                          onChange={(e) => handleEditChange("state_ui_taxable", e.target.value)}
                        />
                      ) : (
                        `$${fmt(r.state_ui_taxable)}`
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editForm.federal_ui}
                          onChange={(e) => handleEditChange("federal_ui", e.target.value)}
                        />
                      ) : (
                        `$${fmt(r.federal_ui)}`
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editForm.taxable_wages}
                          onChange={(e) => handleEditChange("taxable_wages", e.target.value)}
                        />
                      ) : (
                        `$${fmt(r.taxable_wages)}`
                      )}
                    </td>

                    <td>
                      {isEditing ? (
                        <>
                          <button onClick={saveEdit} disabled={saving}>
                            {saving ? "Saving..." : "Save"}
                          </button>
                          <button onClick={cancelEdit} disabled={saving} style={{ marginLeft: 8 }}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button onClick={() => startEdit(r)} disabled={saving}>
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default UIWagesPage;



// import React, { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import api from "../services/api";


// const UIWagesPage = () => {
//   const { id: businessId } = useParams();

//   const [payDates, setPayDates] = useState([]);
//   const [selectedPayDate, setSelectedPayDate] = useState(""); // ALWAYS "YYYY-MM-DD"
//   const [rows, setRows] = useState([]);

//   const [loadingDates, setLoadingDates] = useState(true);
//   const [loadingRows, setLoadingRows] = useState(false);
//   const [error, setError] = useState("");

//   // Convert anything (Date / ISO / mysql date) -> "YYYY-MM-DD"
//   const toYYYYMMDD = (value) => {
//     if (!value) return "";

//     // already in YYYY-MM-DD
//     if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

//     // ISO string like 2025-09-02T04:00:00.000Z
//     if (typeof value === "string" && value.includes("T")) return value.split("T")[0];

//     const d = new Date(value);
//     if (Number.isNaN(d.getTime())) return "";

//     const yyyy = d.getFullYear();
//     const mm = String(d.getMonth() + 1).padStart(2, "0");
//     const dd = String(d.getDate()).padStart(2, "0");
//     return `${yyyy}-${mm}-${dd}`;
//   };

//   const displayDate = (yyyyMmDd) => {
//     if (!yyyyMmDd) return "";
//     const d = new Date(`${yyyyMmDd}T00:00:00`);
//     return d.toLocaleDateString();
//   };

//   useEffect(() => {
//     const fetchPayDates = async () => {
//       try {
//         setLoadingDates(true);
//         setError("");

//         // Backend returns [{ PAY_DATE: "2025-03-03" }, ...]
//         const res = await api.get(`/payroll/paydates/${businessId}`);
//         const dates = res.data || [];
//         setPayDates(dates);

//         if (dates.length > 0) {
//           const first = toYYYYMMDD(dates[0].PAY_DATE || dates[0].pay_date);
//           setSelectedPayDate(first);
//         }
//       } catch (err) {
//         console.error("Fetch pay dates error:", err);
//         setError("Failed to fetch pay dates.");
//       } finally {
//         setLoadingDates(false);
//       }
//     };

//     fetchPayDates();
//   }, [businessId]);

//   useEffect(() => {
//     const fetchRows = async () => {
//       if (!selectedPayDate) return;

//       try {
//         setLoadingRows(true);
//         setError("");

//         // ALWAYS send YYYY-MM-DD
//         const payDateParam = toYYYYMMDD(selectedPayDate);

//         const res = await api.get(`/payroll/ui-wages/${businessId}`, {
//           params: { pay_date: payDateParam },
//         });

//         setRows(res.data || []);
//       } catch (err) {
//         console.error("Fetch UI wages error:", err);
//         setError("Failed to fetch UI / taxable wages for this pay date.");
//       } finally {
//         setLoadingRows(false);
//       }
//     };

//     fetchRows();
//   }, [businessId, selectedPayDate]);

//   const fmt = (v) => {
//     const n = Number(v);
//     if (Number.isNaN(n)) return "0.00";
//     return n.toFixed(2);
//   };

//   if (loadingDates) return <p>Loading pay dates...</p>;

//   return (
//     <div className="uiw-body">
//       <h2>UI / Taxable Wages</h2>

//       {error ? <p className="uiw-error">{error}</p> : null}

//       <div className="uiw-controls">
//         <label>Pay Date:</label>
//         <select
//           value={selectedPayDate}
//           onChange={(e) => setSelectedPayDate(toYYYYMMDD(e.target.value))}
//           disabled={payDates.length === 0}
//         >
//           {payDates.length === 0 ? (
//             <option value="">No pay dates found</option>
//           ) : (
//             payDates.map((d) => {
//               const raw = d.PAY_DATE || d.pay_date;
//               const val = toYYYYMMDD(raw); // dropdown value is YYYY-MM-DD
//               return (
//                 <option key={val} value={val}>
//                   {displayDate(val)}
//                 </option>
//               );
//             })
//           )}
//         </select>
//       </div>

//       {loadingRows ? (
//         <p>Loading employees...</p>
//       ) : (
//         <table className="uiw-table">
//           <thead>
//             <tr>
//               <th>Employee</th>
//               <th>State UI</th>
//               <th>State UI Taxable</th>
//               <th>Federal UI</th>
//               <th>Taxable Wages</th>
//             </tr>
//           </thead>
//           <tbody>
//             {rows.length === 0 ? (
//               <tr>
//                 <td colSpan="5" style={{ textAlign: "center" }}>
//                   No payroll records found for this pay date.
//                 </td>
//               </tr>
//             ) : (
//               rows.map((r) => (
//                 <tr key={r.ID || r.id}>
//                   <td>{r.NAME || r.name}</td>
//                   <td>${fmt(r.state_ui)}</td>
//                   <td>${fmt(r.state_ui_taxable)}</td>
//                   <td>${fmt(r.federal_ui)}</td>
//                   <td>${fmt(r.taxable_wages)}</td>
//                 </tr>
//               ))
//             )}
//           </tbody>
//         </table>
//       )}
//     </div>
//   );
// };

// export default UIWagesPage;

